import { DownloadSimpleIcon, MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import api from '@/api/api'
import { getClientsWithCount } from '@/api/clients/get-clients-with-count'
import {
  GET_GIFT_CARDS_BASE_QUERY_KEY,
  GiftCardModel,
  useGetGiftCards,
} from '@/api/gift-cards/get-gift-cards'
import { getCsvFields } from '@/components/BuildingDashboard/Rewards/helpers'
import RewardsGiftDetailsModal from '@/components/BuildingDashboard/Rewards/RewardsGiftDetailsModal/RewardsGiftDetailsModal'
import RewardsSendGiftModal from '@/components/BuildingDashboard/Rewards/RewardsSendGiftModal/RewardsSendGiftModal'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Select, useSelectOptions } from '@/components/ui/select'
import { StatusTabs } from '@/components/ui/status-tabs'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { buildCsvFilename, downloadCsv, getApiErrorMessage } from '@/utils'

import {
  ADDITIONAL_FILTERS_DROPDOWN_WIDTH,
  GIFT_CARDS_DASHBOARD_ID,
  getGiftCardCategoryOptions,
  getGiftCardStatusOptions,
  getGiftCardsColumns,
  getGiftCardsTypeTabs,
  giftCardsDashboardFiltersSchema,
  initialGiftCardsDashboardFilters,
} from './gift-cards-dashboard.constants'
import { GiftCardsDashboardProps } from './gift-cards-dashboard.types'

export const GiftCardsDashboard = ({ resident, building }: GiftCardsDashboardProps = {}) => {
  const { t } = useTranslation(['gift-cards', 'rewards-table'])
  const [isShowSendGift, setShowSendGift] = useState(false)
  const [residentsSearch, setResidentsSearch] = useState('')

  const residentId = resident?.id
  const globalBuilding = useSelector(selectActiveBuilding)
  const selectedBuilding = building ?? globalBuilding
  const buildingId = selectedBuilding?.id
  const timezone = selectedBuilding?.timezone || 'America/Chicago'

  const searchParams = useSearchParams({
    initialState: initialGiftCardsDashboardFilters,
    schema: giftCardsDashboardFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: initialGiftCardsDashboardFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const type = filters.getFilter('type')
  const category = filters.getFilter('category')
  const status = filters.getFilter('status')
  const search = filters.getFilter('search')
  const clientId = residentId ? [residentId] : filters.getFilter('clientId')

  const queryParams = {
    buildingId,
    category: category || undefined,
    clientId,
    limit: filters.getFilter('limit'),
    query: search || undefined,
    skip: filters.getFilter('skip'),
    status: status || undefined,
    type: type || undefined,
  }

  const { data, isFetching, error, refetch } = useGetGiftCards(queryParams)
  const { data: giftCards = [], count = 0 } = data ?? {}

  const residentsOptions = useSelectOptions(
    {
      query: (params, signal) => getClientsWithCount({ ...params, buildingId }, signal),
      transform: (clientsData) =>
        (clientsData?.clients ?? []).map((client) => ({
          label: `${client.user.firstName} ${client.user.lastName}`,
          value: Number(client.id),
        })),
    },
    {
      enabled: !!buildingId && !residentId,
      queryKey: ['getClientsWithCount', 'gift-cards', buildingId, residentsSearch],
    },
  )

  const recipientColumnSettings = usePersonColumnSettings({
    column: 'recipient',
    dashboard: 'Rewards',
    fieldType: 'resident',
    displays: giftCards.map((card) => card.recipientDisplay),
    rowsQueryKey: [GET_GIFT_CARDS_BASE_QUERY_KEY],
  })
  const columns = getGiftCardsColumns({ recipientColumnSettings, t, timezone })
  const typeTabs = getGiftCardsTypeTabs(t).map((tab) => ({ ...tab, count: undefined }))
  const statusOptions = getGiftCardStatusOptions(t)
  const categoryOptions = getGiftCardCategoryOptions(t)

  const handleTabChange = (value: string) => {
    filters.setFilter('type', value)
  }

  const handleStatusChange = (value: string) => {
    filters.setFilter('status', value)
  }

  const handleCategoryChange = (value: string) => {
    filters.setFilter('category', value)
  }

  const handleResidentChange = (residentIds: number[]) => {
    filters.setFilter('clientId', residentIds)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const onSendEmail = async (rewardId: string) => {
    try {
      await api.resendGiftCardEmail(rewardId)
      toastFactory.success(t('gift-cards:snackbar.emailSent'))
    } catch (sendEmailError) {
      toastFactory.error(getApiErrorMessage(sendEmailError))
    }
  }

  const rowActions: RowAction<GiftCardModel>[] = [
    {
      label: t('gift-cards:rowActions.details'),
      onClick: (row) => handleOpenGiftCardDetails(row.id),
      primary: true,
    },
    {
      hidden: (row) => row.runaProductCode !== 'VISA-US' && row.status !== 'not-redeemed',
      label: t('gift-cards:rowActions.sendEmail'),
      onClick: (row) => void onSendEmail(row.id),
    },
  ]

  const handleDownloadCSV = () => {
    if (!giftCards.length) return
    downloadCsv({
      data: giftCards,
      fields: getCsvFields(timezone, t),
      filename: buildCsvFilename({
        building: selectedBuilding?.name,
        date: new Date(),
        page: 'gift-cards',
      }),
    })
  }

  const handleOpenGiftCardDetails = (cardId: number) => {
    if (!selectedBuilding) {
      return
    }

    popupFactory.open(RewardsGiftDetailsModal, {
      id: cardId,
      timezone: selectedBuilding.timezone!,
    })
  }

  return (
    <>
      <TableGroup
        id={GIFT_CARDS_DASHBOARD_ID}
        columns={columns}
        data={giftCards}
        error={error}
        loading={isFetching}
        getRowKey={(card) => card.id}
        rowActions={rowActions}
        mode="regular"
        title={
          residentId && (
            <Typography as="h2" size="heading-small" color="black800">
              {t('gift-cards:pageTitle')}
            </Typography>
          )
        }
        titleActions={
          residentId && (
            <Button size="small" onClick={() => setShowSendGift(true)}>
              {t('gift-cards:sendGiftButton')}
              <Icon icon={PlusIcon} />
            </Button>
          )
        }
        emptyNoFilters={{
          description: t('gift-cards:empty.description'),
          title: t('gift-cards:empty.title'),
        }}
        filters={
          <>
            <Select
              options={statusOptions}
              placeholder={t('gift-cards:filters.statusPlaceholder')}
              size="small"
              value={status ?? null}
              onChange={handleStatusChange}
              onReset={() => handleStatusChange('')}
            />
            <Select
              options={categoryOptions}
              placeholder={t('gift-cards:filters.categoryPlaceholder')}
              size="small"
              value={category ?? null}
              onChange={handleCategoryChange}
              onReset={() => handleCategoryChange('')}
            />
            {!residentId && (
              <Select
                {...residentsOptions.selectProps}
                searchable
                type="multi"
                dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
                disabled={residentsOptions.isFetching}
                placeholder={t('gift-cards:filters.residentPlaceholder')}
                size="small"
                value={filters.getFilter('clientId') ?? []}
                onChange={handleResidentChange}
                onReset={() => handleResidentChange([])}
                onSearchChange={setResidentsSearch}
              />
            )}
          </>
        }
        navigation={{
          actions: (
            <>
              <Button
                onlyBox
                size="small"
                use="secondary"
                title={t('gift-cards:csv')}
                disabled={!giftCards.length}
                onClick={handleDownloadCSV}
              >
                <Icon icon={DownloadSimpleIcon} />
              </Button>
              {!residentId && (
                <Button size="small" onClick={() => setShowSendGift(true)}>
                  {t('gift-cards:sendGiftButton')}
                  <Icon icon={PlusIcon} />
                </Button>
              )}
            </>
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              placeholder={t('gift-cards:filters.searchPlaceholder')}
              size="small"
              value={search}
              onChange={handleSearchChange}
            />
          ),
          statusTabs: (
            <StatusTabs options={typeTabs} value={type ?? ''} onChange={handleTabChange} />
          ),
        }}
        pagination={{
          paginationFilters: filters,
          total: count,
        }}
        tableFilters={filters}
      />
      {isShowSendGift && (
        <RewardsSendGiftModal
          selectedBuilding={selectedBuilding}
          lockResident={
            resident
              ? {
                  email: resident.email,
                  label: resident.name,
                  phone: resident.phone ?? undefined,
                  value: resident.id,
                }
              : undefined
          }
          onClose={() => setShowSendGift(false)}
          onSuccess={() => void refetch()}
        />
      )}
    </>
  )
}
